import { ViewIcon } from "@chakra-ui/icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Box,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { ChatState } from "../../Context/ChatProvider";
import UserBadgeItem from "../userAvatar/UserBadgeItem";
import GroupChatAvatar from "../userAvatar/GroupChatAvatar";
import UserListItem from "../userAvatar/UserListItem";

const UpdateGroupChatModal = ({ fetchMessages, fetchAgain, setFetchAgain }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [groupChatName, setGroupChatName] = useState("");
  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renameloading, setRenameLoading] = useState(false);
  const [groupDescription, setGroupDescription] = useState("");
  const [groupPic, setGroupPic] = useState("");
  const [picUploading, setPicUploading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const toast = useToast();
  const searchTimeout = useRef(null);

  const { selectedChat, setSelectedChat, user, setChats } = ChatState();

  const bumpFetchAgain = () => {
    if (typeof setFetchAgain === "function") {
      setFetchAgain((prev) => (typeof prev === "number" ? prev + 1 : !prev));
    }
  };

  const upsertChatList = (chat) => {
    if (!chat?._id || typeof setChats !== "function") return;
    const id = String(chat._id);
    setChats((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const rest = list.filter((c) => String(c._id) !== id);
      return [chat, ...rest];
    });
  };

  const isAdmin =
    String(selectedChat?.groupAdmin?._id || selectedChat?.groupAdmin || "") ===
      String(user?._id || "") && !!user?._id;

  // Prefill the profile editor whenever the modal opens.
  useEffect(() => {
    if (isOpen && selectedChat) {
      setGroupDescription(selectedChat.description || "");
      setGroupPic(selectedChat.pic || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedChat?._id]);

  const handleSearch = async (query) => {
    setSearch(query);
    if (!query) {
      setSearchResult([]);
      setLoading(false);
      return;
    }

    if (!user?.token) return;

    // Debounce: use `query` directly so results are not one keystroke behind.
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        };
        const { data } = await axios.get(
          `/api/user?search=${encodeURIComponent(query)}`,
          config
        );
        console.log(data);
        setSearchResult(data);
      } catch (error) {
        toast({
          title: "Error Occured!",
          description: "Failed to Load the Search Results",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-left",
        });
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleRename = async () => {
    if (!groupChatName) return;
    if (!selectedChat?._id) return;

    try {
      setRenameLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.put(
        `/api/chat/rename`,
        {
          chatId: selectedChat._id,
          chatName: groupChatName,
        },
        config
      );

      console.log(data._id);
      // setSelectedChat(null);
      setSelectedChat(data);
      upsertChatList(data);
      bumpFetchAgain();
      setRenameLoading(false);
    } catch (error) {
      toast({
        title: "Error Occured!",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setRenameLoading(false);
    }
    setGroupChatName("");
  };

  const handleAvatarUpload = (file) => {
    if (!file) return;
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      toast({
        title: "Please select a JPEG or PNG image",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setPicUploading(true);
    // Same unsigned Cloudinary preset as signup avatars — no new deps.
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "chat-app");
    data.append("cloud_name", "istiaquenuhash");
    fetch("https://api.cloudinary.com/v1_1/istiaquenuhash/image/upload", {
      method: "post",
      body: data,
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.url) throw new Error("Upload failed");
        setGroupPic(res.url.toString());
        toast({
          title: "Avatar uploaded — hit Save profile",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom",
        });
      })
      .catch(() => {
        toast({
          title: "Avatar upload failed",
          description: "You can paste an image URL instead",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
      })
      .finally(() => setPicUploading(false));
  };

  const handleProfileSave = async () => {
    if (!selectedChat?._id) return;
    if (!isAdmin) {
      toast({
        title: "Only admins can update the group profile",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setProfileSaving(true);
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.put(
        `/api/chat/groupprofile`,
        {
          chatId: selectedChat._id,
          description: groupDescription.trim(),
          pic: groupPic.trim(),
        },
        config
      );
      setSelectedChat(data);
      upsertChatList(data);
      bumpFetchAgain();
      toast({
        title: "Group profile updated",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to update group profile",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddUser = async (user1) => {
    if (!selectedChat) return;
    if (selectedChat.users.find((u) => u._id === user1._id)) {
      toast({
        title: "User Already in group!",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    if (selectedChat.groupAdmin?._id !== user?._id) {
      toast({
        title: "Only admins can add someone!",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.put(
        `/api/chat/groupadd`,
        {
          chatId: selectedChat._id,
          userId: user1._id,
        },
        config
      );

      setSelectedChat(data);
      upsertChatList(data);
      bumpFetchAgain();
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error Occured!",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
    setGroupChatName("");
  };

  const handleTransferAdmin = async (user1) => {
    if (!selectedChat?._id || !user1?._id) return;
    if (!isAdmin) {
      toast({
        title: "Only admins can transfer admin!",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.put(
        `/api/chat/groupadmin`,
        {
          chatId: selectedChat._id,
          userId: user1._id,
        },
        config
      );
      setSelectedChat(data);
      upsertChatList(data);
      bumpFetchAgain();
      toast({
        title: `${user1.name || "Member"} is now group admin`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    } catch (error) {
      toast({
        title: "Failed to transfer admin",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
  };

  const handleRemove = async (user1) => {
    if (!selectedChat) return;
    if (selectedChat.groupAdmin?._id !== user?._id && user1._id !== user?._id) {
      toast({
        title: "Only admins can remove someone!",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.put(
        `/api/chat/groupremove`,
        {
          chatId: selectedChat._id,
          userId: user1._id,
        },
        config
      );

      const selfLeave = user1._id === user?._id;
      if (selfLeave) {
        setSelectedChat(null);
        if (typeof setChats === "function") {
          setChats((prev) =>
            Array.isArray(prev)
              ? prev.filter(
                  (c) => String(c._id) !== String(selectedChat._id)
                )
              : prev
          );
        }
        onClose();
      } else {
        // REST may return the updated chat or a deleted-group stub.
        if (data && data._id) {
          setSelectedChat(data);
          upsertChatList(data);
        } else if (data && data.chatId && data.deleted) {
          setSelectedChat(null);
          if (typeof setChats === "function") {
            setChats((prev) =>
              Array.isArray(prev)
                ? prev.filter((c) => String(c._id) !== String(data.chatId))
                : prev
            );
          }
        } else {
          setSelectedChat(data);
        }
      }
      bumpFetchAgain();
      if (!selfLeave && typeof fetchMessages === "function") {
        fetchMessages();
      }
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error Occured!",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
    setGroupChatName("");
  };

  return (
    <>
      <IconButton
        display={{ base: "flex" }}
        icon={<ViewIcon />}
        aria-label="Group settings"
        variant="ghost"
        color="var(--ch-accent)"
        _hover={{ bg: "var(--ch-hover-ghost)" }}
        onClick={onOpen}
      />

      <Modal onClose={onClose} isOpen={isOpen} isCentered>
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader
            fontSize="35px"
            fontFamily="Sora"
            fontWeight="800"
            display="flex"
            justifyContent="center"
          >
            {selectedChat?.chatName || "Group Chat"}
          </ModalHeader>

          <ModalCloseButton />
          <ModalBody display="flex" flexDir="column" alignItems="center">
            <Box w="100%" display="flex" alignItems="center" gap={3} pb={3}>
              <GroupChatAvatar
                users={selectedChat?.users}
                name={selectedChat?.chatName}
                pic={groupPic || selectedChat?.pic}
                size={64}
                currentUserId={user?._id}
              />
              <Box flex="1" minW={0}>
                {isAdmin ? (
                  <>
                    <FormControl>
                      <FormLabel fontSize="xs" color="var(--ch-faint)" mb={1}>
                        Group avatar
                      </FormLabel>
                      <Input
                        type="file"
                        size="sm"
                        p={1}
                        className="chat-input"
                        accept="image/jpeg,image/png"
                        onChange={(e) => handleAvatarUpload(e.target.files[0])}
                        isDisabled={picUploading}
                      />
                    </FormControl>
                    <Input
                      size="sm"
                      mt={2}
                      className="chat-input"
                      placeholder="…or paste an image URL"
                      value={groupPic}
                      onChange={(e) => setGroupPic(e.target.value)}
                    />
                  </>
                ) : (
                  selectedChat?.description && (
                    <Box fontSize="sm" color="var(--ch-dim)">
                      {selectedChat.description}
                    </Box>
                  )
                )}
              </Box>
            </Box>
            {isAdmin ? (
              <FormControl mb={2}>
                <FormLabel fontSize="xs" color="var(--ch-faint)" mb={1}>
                  Group description
                </FormLabel>
                <Textarea
                  size="sm"
                  className="chat-input"
                  placeholder="What is this group about? (500 chars max)"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  rows={2}
                />
                <Button
                  size="sm"
                  mt={2}
                  className="chat-btn-primary"
                  onClick={handleProfileSave}
                  isLoading={profileSaving || picUploading}
                >
                  Save profile
                </Button>
              </FormControl>
            ) : null}
            <Box w="100%" display="flex" flexWrap="wrap" pb={3}>
              {selectedChat?.users?.map((u) => (
                <UserBadgeItem
                  key={u._id}
                  user={u}
                  admin={selectedChat?.groupAdmin?._id}
                  handleFunction={() => handleRemove(u)}
                />
              ))}
            </Box>
            {isAdmin && (
              <Box w="100%" pb={2}>
                {selectedChat?.users
                  ?.filter(
                    (u) =>
                      String(u._id) !==
                      String(
                        selectedChat?.groupAdmin?._id ||
                          selectedChat?.groupAdmin
                      )
                  )
                  .map((u) => (
                    <Box
                      key={u._id}
                      w="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      py={1}
                    >
                      <Box fontSize="sm" color="var(--ch-text)" isTruncated>
                        {u.name}
                      </Box>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="var(--ch-accent)"
                        _hover={{ bg: "var(--ch-hover-ghost)" }}
                        onClick={() => handleTransferAdmin(u)}
                      >
                        Make admin
                      </Button>
                    </Box>
                  ))}
              </Box>
            )}
            <FormControl display="flex">
              <Input
                placeholder="Chat Name"
                mb={3}
                className="chat-input"
                value={groupChatName}
                onChange={(e) => setGroupChatName(e.target.value)}
              />
              <Button
                className="chat-btn-primary"
                ml={1}
                isLoading={renameloading}
                onClick={handleRename}
              >
                Update
              </Button>
            </FormControl>
            <FormControl>
              <Input
                placeholder="Add User to group"
                mb={1}
                className="chat-input"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </FormControl>

            {loading ? (
              <Spinner size="lg" />
            ) : (
              searchResult?.map((user) => (
                <UserListItem
                  key={user._id}
                  user={user}
                  handleFunction={() => handleAddUser(user)}
                />
              ))
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => handleRemove(user)} colorScheme="red">
              Leave Group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateGroupChatModal;
